import React, { useState, useRef, useEffect } from 'react';
import { formatDisplayName } from '../utils/displayName';

interface Attendee {
  id: string;
  username: string;
  name?: string | null;
  surname?: string | null;
}

interface AttendeesListProps {
  attendees: Attendee[];
  className?: string;
}

const AttendeesList: React.FC<AttendeesListProps> = ({ attendees, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState<string>('0px');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(isExpanded ? `${contentRef.current.scrollHeight}px` : '0px');
    }
  }, [isExpanded, attendees]);

  if (attendees.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className={className}>
      <button
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-white/30 active:bg-white/40 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded px-1 py-0.5 -mx-1"
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Hide attendees' : 'Show attendees'}
      >
        <span className="flex items-center gap-1.5">
          <span className="font-semibold">Attendees</span>
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-gray-200 text-gray-700 rounded-full text-[10px] font-semibold">
            {attendees.length}
          </span>
        </span>
        <svg
          className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight }}
        aria-hidden={!isExpanded}
      >
        <div ref={contentRef} className="pt-1.5">
          <div className="text-xs text-gray-600 space-y-1 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
            {attendees.map((attendee, index) => (
              <div
                key={attendee.id}
                className="py-0.5 px-1.5 rounded bg-white/50 border border-gray-200/50 hover:bg-white/70 hover:border-gray-300/50 transition-all duration-150"
                style={{
                  animation: isExpanded
                    ? `fadeInUp 0.3s ease-out ${Math.min(index * 0.02, 0.3)}s both`
                    : 'none',
                }}
              >
                {formatDisplayName(attendee)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(156, 163, 175, 0.7);
        }
      `}</style>
    </div>
  );
};

export default AttendeesList;

